// frontend/src/components/detail/tables/TriggersTable.jsx
import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { DataTable } from '../../tablekit';
import { slugify } from '../../../utils/slug';

/* -------------------- width map (edit here) -------------------- */
const W = {
  parent: {
    triggerText: { minWidth: 280 },               // Trigger
    occurrences: { width: 140, align: 'right' },  // Occurrences
    quoteText: { minWidth: 320 },                 // Representative quote
    open: { width: 64, align: 'center' },         // Open
  },
  child: {
    name: { width: 160 },                          // Name
    demographics: { minWidth: 260 },               // Demographics
    date: { width: 120 },                          // Date
    trigger: { minWidth: 280 },                    // Trigger (raw)
    open: { width: 64, align: 'center' },          // Open
  }
};

/* -------------------- helpers -------------------- */

// Flatten segmentation_data into a readable single line
function formatDemographics(seg) {
  if (!seg || typeof seg !== 'object') return '—';
  const entries = Object.entries(seg)
    .map(([k, v]) => {
      if (v == null) return null;
      if (Array.isArray(v)) {
        const list = v.map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
        return `${k}: ${list}`;
      }
      if (typeof v === 'object') {
        const inner = Object.entries(v)
          .map(([ik, iv]) => `${ik}: ${Array.isArray(iv) ? iv.join(', ') : String(iv)}`)
          .join('; ');
        return `${k}: { ${inner} }`;
      }
      return `${k}: ${String(v)}`;
    })
    .filter(Boolean);
  return entries.length ? entries.join(' • ') : '—';
}

/** Build instances (interviews) for a trigger category from stories */
function buildInstancesFromStories({ customerStories = [], triggerCategory = '' }) {
  const target = slugify(triggerCategory);
  const out = [];
  for (const s of customerStories) {
    const top = s?.trigger_category;
    const arrays = [s?.triggers, s?.switching_triggers, s?.why_customers_switch].filter(Boolean);
    let matched = false;

    if (top && slugify(top) === target) matched = true;
    if (!matched) {
      for (const arr of arrays) {
        for (const t of arr) {
          const cat = t?.trigger_category || t?.title || t?.reason || t?.text || '';
          if (cat && slugify(cat) === target) { matched = true; break; }
        }
        if (matched) break;
      }
    }
    if (!matched) continue;

    out.push({
      key: `${s.customer_name || 'Interview'}-${s.conversation_date || ''}`,
      name: s.customer_name || 'Interview',
      date: s.conversation_date || '',
      demographics: formatDemographics(s.segmentation_data),
      trigger: s.trigger || top || '', // raw text if present
      href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
      rawStory: s
    });
  }
  // newest first
  out.sort((a, b) => (Date.parse(b.date || '') || 0) - (Date.parse(a.date || '') || 0));
  return out;
}

/** Choose a representative quote for a trigger from stories */
function pickRepresentativeQuote({ customerStories = [], triggerCategory = '' }) {
  const target = slugify(triggerCategory);
  for (const s of customerStories) {
    if (s.trigger && (slugify(s.trigger_category || '') === target)) {
      return { text: String(s.trigger), url: '', timestamp: s.trigger_timestamp || '' };
    }
    const arrays = [s.triggers, s.switching_triggers, s.why_customers_switch].filter(Boolean);
    for (const arr of arrays) {
      for (const t of arr) {
        const cat = t?.trigger_category || t?.title || t?.reason || t?.text || '';
        if (cat && slugify(cat) === target) {
          const text = t?.trigger || t?.text || cat;
          const url = t?.url || '';
          const timestamp = t?.timestamp || t?.time || '';
          return { text: String(text || ''), url, timestamp };
        }
      }
    }
  }
  return { text: '', url: '', timestamp: '' };
}

/** Aggregate parent rows from stories */
function buildFromStories(customerStories = []) {
  const counts = new Map(); // key -> { triggerText, occurrences }
  const byKeyToTitle = new Map();

  const bump = (title) => {
    if (!title) return;
    const clean = String(title).trim();
    const key = slugify(clean);
    counts.set(key, (counts.get(key) || 0) + 1);
    if (!byKeyToTitle.has(key)) byKeyToTitle.set(key, clean);
  };

  for (const s of customerStories) {
    if (s.trigger_category) bump(s.trigger_category);
    const arrays = [s.triggers, s.switching_triggers, s.why_customers_switch].filter(Boolean);
    for (const arr of arrays) {
      for (const t of arr) bump(t?.trigger_category || t?.title || t?.reason || t?.text);
    }
  }

  const rows = [];
  for (const [key, count] of counts.entries()) {
    const triggerText = byKeyToTitle.get(key) || '—';
    const rep = pickRepresentativeQuote({ customerStories, triggerCategory: triggerText });
    rows.push({
      key,
      triggerText,
      occurrences: count,
      quoteText: rep.text || '—',
      quoteUrl: rep.url || '',
      timestamp: rep.timestamp || '',
      linkTo: `/trigger/${key}`,
      instances: buildInstancesFromStories({ customerStories, triggerCategory: triggerText })
    });
  }

  // sort by occurrences desc, then alpha
  rows.sort((a, b) => (b.occurrences - a.occurrences) || a.triggerText.localeCompare(b.triggerText));
  return rows;
}

/* -------------------- component -------------------- */

export default function TriggersTable({
  /** Optional legacy source (e.g., tiles). If not provided, we build from stories. */
  rowsSource = [],
  /** Customer stories to build the table from. */
  customerStories = [],
  title = 'Triggers'
}) {
  const location = useLocation();

  const rows = React.useMemo(() => {
    if (Array.isArray(rowsSource) && rowsSource.length) {
      // Legacy mapping path (kept for compatibility)
      return rowsSource.map((t, i) => {
        const fallbackTitle = t.trigger || t.title || t.trigger_text || '';
        const occurrences = t.occurrences ?? t.frequency ?? t.count ?? 0;

        const repNode = t.representativeQuote ?? t.quote ?? t.representativeReceipt ?? t.receipts ?? '';
        const rep = typeof repNode === 'string'
          ? { text: repNode, url: '', timestamp: '' }
          : Array.isArray(repNode)
          ? { text: (repNode[0]?.quote || repNode[0]?.text || ''), url: repNode[0]?.url || '', timestamp: repNode[0]?.timestamp || '' }
          : { text: repNode?.quote || repNode?.text || '', url: repNode?.url || '', timestamp: repNode?.timestamp || '' };

        const category = t.trigger_category || t.category || fallbackTitle || null;
        const key = slugify(category || fallbackTitle);
        const instances = buildInstancesFromStories({ customerStories, triggerCategory: category || '' });

        return {
          key,
          triggerText: category || fallbackTitle || '—',
          occurrences,
          quoteText: rep.text || '—',
          quoteUrl: rep.url || '',
          timestamp: rep.timestamp || '',
          linkTo: `/${'trigger'}/${key}${location.search || ''}`,
          instances,
          raw: t
        };
      });
    }
    // Build from stories
    const storyRows = buildFromStories(customerStories);
    // preserve any query params
    return storyRows.map(r => ({ ...r, linkTo: `${r.linkTo}${location.search || ''}` }));
  }, [rowsSource, customerStories, location.search]);

  if (!rows.length) {
    return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No triggers found.</Typography>;
  }

  // parent columns
  const columns = [
    { key: 'triggerText', header: 'Trigger', ...W.parent.triggerText },
    { key: 'occurrences', header: 'Occurrences', type: 'number', align: 'right', ...W.parent.occurrences },
    {
      key: 'quoteText',
      header: 'Representative quote',
      accessor: (r) => r.quoteText || '—',
      render: (text, r) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {text || '—'} {r.timestamp ? `• ${r.timestamp}` : ''}
        </Typography>
      ),
      ...W.parent.quoteText
    },
    {
      key: 'linkTo',
      header: 'Open',
      accessor: (r) => r.linkTo || '',
      align: 'center',
      render: (href) =>
        href ? (
          <Tooltip title="Open trigger details">
            <IconButton size="small" component={RouterLink} to={href} aria-label="Open trigger details">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : '—',
      ...W.parent.open
    }
  ];

  // sub-table columns (instances)
  const subTables = [
    {
      key: 'instances',
      title: 'Occurrences',
      columns: [
        { key: 'name', header: 'Name', ...W.child.name },
        { key: 'demographics', header: 'Demographics', ...W.child.demographics },
        { key: 'date', header: 'Date', type: 'date', ...W.child.date },
        { key: 'trigger', header: 'Trigger (raw)', ...W.child.trigger },
        {
          key: 'href',
          header: 'Open',
          accessor: (r) => r.href || '',
          align: 'center',
          render: (href) =>
            href ? (
              <Tooltip title="Open interview">
                <IconButton size="small" component="a" href={href} target="_blank" rel="noopener" aria-label="Open interview">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : '—',
          ...W.child.open
        }
      ]
    }
  ];

  return (
    <DataTable
      title={title}
      columns={columns}
      rows={rows}
      subTables={subTables}
      dense
    />
  );
}
